// JS wrapper for the native shared bottom-navigation shell (Android
// only). Modeled directly on src/plugins/NativeNotificationCenter.js's
// proven shape - availability check, never-throw helpers, and the same
// proactive-event + pull-based-consume pattern for native-originated tab
// taps (see NativeShellController.js for the one place that calls these).

import { Capacitor } from "@capacitor/core";

function getNativeBottomNavPlugin() {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.Capacitor?.Plugins?.NativeBottomNav ?? null;
  } catch {
    return null;
  }
}

export function isNativeBottomNavAvailable() {
  return Capacitor.getPlatform() === "android" && !!getNativeBottomNavPlugin();
}

/**
 * Establishes a new shell session - see NativeShellController.js for
 * when this is called (once per controller lifetime: on mount, and again
 * on every uid change) and NativeBottomNavPlugin.java's own class doc for
 * why an explicit session boundary is required (a WebView/React reload
 * without Android process death would otherwise strand every subsequent
 * setShellState call as permanently "stale" against native's own
 * static, process-lifetime routeRevision high-water mark). Must be
 * awaited and confirmed before the caller ever calls setShellState for
 * this session.
 *
 * sessionGeneration fences this specific call against an old, delayed
 * begin request reclaiming ownership after a newer session has already
 * begun (see NativeBottomNavPlugin.beginShellSession's own doc for why
 * this is required in addition to the shellSessionId identity check
 * setShellState/endShellSession use) - native uses strict generation
 * semantics (see ShellSessionGate.decideBegin): a strictly greater
 * generation always wins; an EQUAL generation is only ever accepted as an
 * idempotent retry when shellSessionId also matches exactly (never a
 * bare >= rule); anything else (equal generation with a different
 * session id, or a lower generation) is rejected. Must be issued via
 * issueShellSessionGeneration (src/plugins/shellSessionGeneration.js) -
 * never raw Date.now(), which two sessions issued within the same
 * millisecond could otherwise collide on - never derived from
 * uid/email/token/user data.
 *
 * A REJECTED result is deliberately still returned (never collapsed to
 * null) whenever native's response is otherwise trustworthy - see
 * NativeShellController.js's own establishment logic, which inspects a
 * rejection's echoed shellSessionId/sessionGeneration/routeRevision to
 * decide whether to rebase its local generator (see
 * advanceShellSessionGenerationPast in shellSessionGeneration.js) and
 * retry once. Only a genuinely UNUSABLE response (missing plugin,
 * non-Android, a thrown/rejected call, or a response missing/malformed
 * shellSessionId/sessionGeneration entirely) collapses to null - and an
 * ACCEPTED result is only ever trusted if it exactly echoes the
 * shellSessionId/sessionGeneration THIS call itself requested (an
 * accepted response for anything else is nonsensical and treated as
 * unusable, same as a genuine failure).
 *
 * @param {string} shellSessionId an opaque, non-sensitive session
 *     identifier - never derived from uid/email/token/user data.
 * @param {number} sessionGeneration a strictly-increasing fencing value.
 * @returns {Promise<{accepted: boolean, shellSessionId: string, sessionGeneration: number, routeRevision: number|null}|null>}
 *     null only when the response is genuinely unusable (see above) -
 *     never throws. accepted:false with a valid shellSessionId/
 *     sessionGeneration means an equal-or-newer session already won the
 *     race; those fields describe whichever session is ACTUALLY current
 *     on native's side right now.
 */
export async function beginShellSession(shellSessionId, sessionGeneration) {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.beginShellSession !== "function") {
    return null;
  }
  try {
    const result = await plugin.beginShellSession({ shellSessionId, sessionGeneration });
    if (!result || typeof result !== "object") {
      return null;
    }

    const echoedSessionId = typeof result.shellSessionId === "string" ? result.shellSessionId : null;
    const echoedGeneration =
      typeof result.sessionGeneration === "number" && Number.isFinite(result.sessionGeneration)
        ? result.sessionGeneration
        : null;
    if (echoedSessionId === null || echoedGeneration === null) {
      // No usable session identity/generation at all - malformed,
      // whether accepted or rejected. Never guessed at, never retried
      // from.
      return null;
    }
    const echoedRouteRevision =
      typeof result.routeRevision === "number" && Number.isFinite(result.routeRevision)
        ? result.routeRevision
        : null;

    const accepted = result.accepted === true;
    if (accepted && (echoedSessionId !== shellSessionId || echoedGeneration !== sessionGeneration)) {
      // An "accepted" response that doesn't echo exactly what THIS call
      // requested is nonsensical - treat it as unusable, same as a
      // genuine failure, never as a session this caller can act on.
      return null;
    }

    return {
      accepted,
      shellSessionId: echoedSessionId,
      sessionGeneration: echoedGeneration,
      routeRevision: echoedRouteRevision,
    };
  } catch {
    return null;
  }
}

/**
 * Controller-lifecycle counterpart to clearShellState's unconditional
 * logout reset - see NativeBottomNavPlugin.endShellSession's own doc.
 * Best-effort, never throws. Called from NativeShellController.js's own
 * cleanup (unmount, and every uid change) for the session that controller
 * instance is leaving behind - a no-op on native if a newer session has
 * already superseded it.
 *
 * @param {string} shellSessionId the session this caller itself
 *     established (via beginShellSession) and is now leaving behind.
 */
export async function endShellSession(shellSessionId) {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.endShellSession !== "function") {
    return;
  }
  try {
    await plugin.endShellSession({ shellSessionId });
  } catch {
    // best-effort only
  }
}

/**
 * The durable readiness proof - see NativeShellController.js. Native
 * only ever returns applied:true after MainShellCoordinator has
 * genuinely finished applying the state on the Android UI thread; the
 * separate "shellReady" event is a best-effort nudge only and is never
 * required for correctness (a fire-and-forget event has no delivery
 * guarantee to a JS listener that attaches after it fires, which a cold
 * start cannot rule out).
 *
 * @param {{
 *   shellSessionId: string,
 *   activeTab: string|null,
 *   barVisible: boolean,
 *   authenticated: boolean,
 *   accountAccess: "allowed"|"blocked"|"unknown",
 *   theme: "male"|"female"|"default",
 *   unreadCount: number,
 *   fullScreenSuppressed: boolean,
 *   routeRevision: number,
 * }} state
 * @returns {Promise<{applied: boolean, shellSessionId: string|null, routeRevision: number}>}
 *     never throws - a missing plugin, a missing coordinator, or a
 *     genuine native error all resolve to applied:false with the
 *     shellSessionId/routeRevision the caller sent, so
 *     NativeShellController's own "is this response for my latest
 *     request" comparison works uniformly regardless of why it failed.
 */
export async function setShellState(state) {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.setShellState !== "function") {
    return {
      applied: false,
      shellSessionId: state.shellSessionId,
      routeRevision: state.routeRevision,
    };
  }
  try {
    const result = await plugin.setShellState(state);
    return {
      applied: result?.applied === true,
      shellSessionId:
        typeof result?.shellSessionId === "string" ? result.shellSessionId : state.shellSessionId,
      routeRevision:
        typeof result?.routeRevision === "number" ? result.routeRevision : state.routeRevision,
    };
  } catch {
    return {
      applied: false,
      shellSessionId: state.shellSessionId,
      routeRevision: state.routeRevision,
    };
  }
}

/** Best-effort, never throws - called on logout/account switch. */
export async function clearShellState() {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.clearShellState !== "function") {
    return;
  }
  try {
    await plugin.clearShellState();
  } catch {
    // best-effort only
  }
}

/**
 * Pull-based, consume-once - the sole source of truth for a native tab
 * tap that resolved to a web-route hand-off. Returns "" when there is
 * nothing pending or the plugin isn't present.
 */
export async function consumePendingShellCommand() {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.consumePendingShellCommand !== "function") {
    return "";
  }
  try {
    const result = await plugin.consumePendingShellCommand();
    return String(result?.tab || "");
  } catch {
    return "";
  }
}

/**
 * Subscribe to the native "shellReady" BEST-EFFORT nudge event - fires
 * once MainShellCoordinator attaches (cold start, and again after any
 * process/Activity recreation). NOT a readiness proof by itself (see
 * setShellState's own doc comment) - a listener attached after this
 * fires may never see it. Response is always the same: resend the
 * current shell state, which will get a fresh, authoritative
 * applied:true/false acknowledgement of its own regardless of whether
 * this event was ever observed.
 */
export function addShellReadyListener(callback) {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }
  return plugin.addListener("shellReady", () => callback());
}

/**
 * Subscribe to the native "shellTabTapped" nudge event - no payload;
 * consumePendingShellCommand() remains the sole source of truth.
 */
export function addShellTabTappedListener(callback) {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }
  return plugin.addListener("shellTabTapped", () => callback());
}

/**
 * Pull-based, consume-once - the sole source of truth for an in-list
 * notification tap (inside MainActivity's own native Notification Center
 * content) that resolved to an already-validated web route (see
 * NativeNotificationCenterController.onItemClicked/
 * NativeBottomNavPlugin.queuePendingNotificationRoute). Replaces the
 * former Intent-based NotificationHandoff/consumeNativeNotificationHandoff
 * chain, which existed only to cross the old NotificationCenterActivity
 * Activity boundary - that boundary no longer exists. Returns "" when
 * there is nothing pending or the plugin isn't present; consuming it
 * clears it, so a stale response can never be replayed.
 */
export async function consumePendingNotificationRoute() {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.consumePendingNotificationRoute !== "function") {
    return "";
  }
  try {
    const result = await plugin.consumePendingNotificationRoute();
    return String(result?.path || "");
  } catch {
    return "";
  }
}

/**
 * Subscribe to the native "notificationRouteChanged" nudge event - no
 * payload; consumePendingNotificationRoute() remains the sole source of
 * truth.
 */
export function addNotificationRouteChangedListener(callback) {
  const plugin = getNativeBottomNavPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }
  return plugin.addListener("notificationRouteChanged", () => callback());
}
